import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BrandLogo } from '../components/BrandLogo';
import { useAuth } from '../auth/AuthContext';
import { api, ApiError } from '../lib/api';
import { checkoutErrText } from '../lib/subscriptionCheckout';
import { appStoreUrl } from '../lib/influencerTracking';

type BillingStatus = {
  email?: string;
  subscribed: boolean;
  source: 'stripe' | 'apple' | null;
  canManageOnWeb: boolean;
};

export function AccountPage() {
  const { user, loading, logout, refresh } = useAuth();
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    void api<BillingStatus>('/api/stripe/billing')
      .then(setBilling)
      .catch(() => setBilling(null));
  }, [user]);

  if (loading) {
    return <div className="app-body flex min-h-screen items-center justify-center text-sm text-zinc-500">Loading…</div>;
  }
  if (!user) return <Navigate to="/login?next=/account" replace />;
  if (!user.onboardingCompleted) return <Navigate to="/onboarding" replace />;

  const subscribed = Boolean(user.aiCoachingEnabled || user.adFreeEnabled);
  const billedViaApple = billing?.source === 'apple';
  const showPortal = subscribed && !billedViaApple;

  async function openBillingPortal() {
    setErr(null);
    setBusy(true);
    try {
      const { url } = await api<{ url: string }>('/api/stripe/create-portal-session', { method: 'POST' });
      if (!url) throw new Error('Billing portal could not start.');
      window.location.href = url;
    } catch (e) {
      setErr(e instanceof ApiError ? checkoutErrText(e) : checkoutErrText(e));
      setBusy(false);
    }
  }

  return (
    <div className="app-body flex min-h-screen flex-col items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md text-center">
        <div className="mb-6 flex justify-center">
          <BrandLogo variant="hero" />
        </div>
        <h1 className="font-display text-2xl font-bold text-[var(--text-primary)]">Your account</h1>
        <p className="mt-3 text-sm leading-relaxed text-[var(--text-muted)]">
          Signed in as <span className="text-zinc-200">{user.email}</span>
        </p>

        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-5 text-left text-sm text-zinc-300">
          <p>
            <strong className="text-white">{subscribed ? 'Subscription active' : 'No active web subscription'}</strong>
          </p>
          {billedViaApple ? (
            <p className="mt-2 text-zinc-500">
              Billed through Apple. Cancel or change payment in iPhone Settings → Apple ID → Subscriptions.
            </p>
          ) : subscribed ? (
            <p className="mt-2 text-zinc-500">
              Update your card, invoices, or cancel on the Stripe billing page.
            </p>
          ) : (
            <p className="mt-2 text-zinc-500">Subscribe on the web to unlock Pro, then download the iOS app.</p>
          )}
        </div>

        {err ? <p className="mt-4 text-sm text-rose-300">{err}</p> : null}

        {showPortal ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void openBillingPortal()}
            className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-sky-400 px-6 py-3.5 text-sm font-semibold text-zinc-950 disabled:opacity-60"
          >
            {busy ? 'Opening billing…' : 'Manage billing'}
          </button>
        ) : null}

        {!subscribed ? (
          <Link
            to="/checkout"
            className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-sky-400 px-6 py-3.5 text-sm font-semibold text-zinc-950"
          >
            Continue to checkout
          </Link>
        ) : (
          <a
            href={appStoreUrl()}
            target="_blank"
            rel="noreferrer"
            className={`${showPortal ? 'mt-3 border border-white/15 bg-transparent text-white' : 'mt-6 bg-sky-400 text-zinc-950'} inline-flex w-full items-center justify-center rounded-full px-6 py-3.5 text-sm font-semibold`}
          >
            Download the iOS app
          </a>
        )}

        <button
          type="button"
          className="mt-4 text-xs text-zinc-500 underline"
          onClick={() => void refresh()}
        >
          Refresh account status
        </button>

        <p className="mt-8 flex items-center justify-center gap-4 text-sm text-[var(--text-muted)]">
          <Link to="/" className="font-semibold text-accent">
            Home
          </Link>
          <button
            type="button"
            className="font-semibold text-zinc-400"
            onClick={() => {
              logout();
              window.location.assign('/');
            }}
          >
            Sign out
          </button>
        </p>
      </motion.div>
    </div>
  );
}
